// P7.5: pure view-model over the POST /api/price-quote response for the booking
// UI (GenerateOffer, reservation.js, calendar ReservationFormView). No network,
// no React — unit-tested in utils/__tests__/priceBreakdown.test.js.
//
// Callers pass their page-local base-rate calc (the getEquipmentDailyRate dupes
// are intentional, see CLAUDE.md), so the breakdown compares server pricing
// against exactly what the page would have shown without a quote. Every builder
// returns null / a pass-through shape when there is no usable quote — callers
// then render the pre-P7.5 UI unchanged.

function round2(n) {
  return Math.round((Number(n) + Number.EPSILON) * 100) / 100;
}

// Badge text for one applied price rule, e.g. "Weekend +15%" / "Promo: 99.00 zł/dzień".
export function formatRuleBadge(rule) {
  if (!rule) return '';
  const value = Number(rule.adjustmentValue) || 0;
  if (rule.adjustmentType === 'fixed') return `${rule.name}: ${value.toFixed(2)} zł/dzień`;
  return `${rule.name} ${value > 0 ? '+' : ''}${value}%`;
}

// Kwota sztuki przy stałej stawce, liczona tak samo jak na serwerze (lib/pricing.js#quoteWithChargeableDays):
// stawka zsumowana dzień po dniu, średnia razy dni płatne. Zwykłe `stawka × dni płatne` zaokrągla pół
// grosza inaczej, a przy ułamkowych dniach płatnych z tabeli firmy pół grosza zdarza się często, więc
// firma bez żadnej reguły widziała „korektę cennika” o grosz. Zgodność z serwerem na pełnym zakresie
// stawek wykonuje Login/backend/tests/dniPlatneLustra.test.js.
function billedAtFlatRate(rate, days, chargeableDays) {
  if (days <= 0) return round2(rate * chargeableDays);
  let sum = 0;
  for (let day = 0; day < days; day += 1) sum += rate;
  return round2((sum / days) * chargeableDays);
}

// "śr." marker — the unit's daily rate varied inside the range: more than one
// rule fired, or a single rule covered only part of the days (uniform
// application over every day would have produced a different average).
// `effectiveDaily` is the quoted total divided back by the paid days, so with fewer paid days than
// one it can differ from the rate by rounding alone: only a different total counts then.
function rateVariesAcrossDays(baseDaily, effectiveDaily, appliedRules, quotedTotal, days, chargeableDays) {
  if (appliedRules.length > 1) return true;
  const rule = appliedRules[0];
  const uniform = rule.adjustmentType === 'fixed'
    ? round2(Number(rule.adjustmentValue) || 0)
    : round2(baseDaily * (1 + (Number(rule.adjustmentValue) || 0) / 100));
  return uniform.toFixed(2) !== effectiveDaily.toFixed(2)
    && billedAtFlatRate(uniform, days, chargeableDays) !== quotedTotal;
}

/**
 * Builds the price-visibility view-model.
 * @param {object|null} quote      fresh /api/price-quote response (items/grandTotal/days/chargeableDays/bundles)
 * @param {Array}       selectedEq full equipment objects currently selected (page order)
 * @param {Function}    getBaseDaily (eq) => local base daily rate the page would render without a quote
 * @returns {object|null} null when the quote is missing/stale/partial — fallback UI applies
 */
export function buildPriceBreakdown(quote, selectedEq, getBaseDaily) {
  if (!quote || !Array.isArray(quote.items)) return null;
  if (!Array.isArray(selectedEq) || !selectedEq.length) return null;
  const chargeableDays = Number(quote.chargeableDays) || 0;
  if (chargeableDays <= 0) return null;
  const days = Number(quote.days) || 0;

  const quoteItemById = new Map(quote.items.map((it) => [it.equipmentId, it]));
  // The quote must price exactly the current selection — a unit missing from the
  // quote OR a quote priced for extra units means it is stale/partial, and the
  // breakdown (base sum vs grandTotal) would not add up.
  if (quoteItemById.size !== selectedEq.length) return null;
  for (const eq of selectedEq) {
    if (!quoteItemById.has(eq.id)) return null;
  }

  const bundles = (Array.isArray(quote.bundles) ? quote.bundles : []).map((b, index) => ({
    key: index,
    bundleId: b.bundleId,
    name: b.name,
    equipmentIds: [...(b.equipmentIds || [])],
    total: round2(b.total),
    dailyRate: round2((Number(b.total) || 0) / chargeableDays),
    // N.2: stawka różniła się między dniami terminu, więc dailyRate to średnia, a nie cena zestawu.
    isAverage: b.rateVaries === true,
    // N.2: reguły „działa też na pakiety”, które zmieniły cenę tego zestawu.
    appliedRules: Array.isArray(b.appliedRules) ? b.appliedRules : [],
    // N.2: suma sztuk liczonych osobno, po regułach (null = serwer tego nie podał).
    partsTotal: Number.isFinite(b.base) ? b.base : null,
  }));
  const bundleKeyByEquipmentId = new Map();
  bundles.forEach((b) => b.equipmentIds.forEach((id) => bundleKeyByEquipmentId.set(id, b.key)));

  let baseTotal = 0;
  let ruleAdjustment = 0; // Σ (quoted − base) over units billed individually; ± sign preserved
  let bundleBase = 0;     // Σ base over units billed at a bundle set price
  const baseByBundle = new Map(); // the same sum per bundle
  const itemsById = {};

  for (const eq of selectedEq) {
    const it = quoteItemById.get(eq.id);
    const baseDaily = round2(getBaseDaily(eq));
    const effectiveDaily = round2(it.dailyAvg);
    const baseItemTotal = billedAtFlatRate(baseDaily, days, chargeableDays);
    const quotedTotal = round2(it.total);
    const bundleKey = bundleKeyByEquipmentId.has(eq.id) ? bundleKeyByEquipmentId.get(eq.id) : null;
    const appliedRules = Array.isArray(it.appliedRules) ? it.appliedRules : [];

    baseTotal += baseItemTotal;
    if (bundleKey == null) {
      ruleAdjustment += (Number(it.total) || 0) - baseItemTotal;
    } else {
      bundleBase += baseItemTotal;
      baseByBundle.set(bundleKey, (baseByBundle.get(bundleKey) || 0) + baseItemTotal);
    }

    itemsById[eq.id] = {
      equipmentId: eq.id,
      baseDaily,
      effectiveDaily,
      total: quotedTotal,
      appliedRules,
      bundleKey,
      // strikethrough only when the two displayed (rounded) values actually differ, and the amount
      // does too: below one paid day the rate read back from the total differs by rounding alone
      differsFromBase: bundleKey == null && baseDaily.toFixed(2) !== effectiveDaily.toFixed(2)
        && quotedTotal !== baseItemTotal,
      isAverage: bundleKey == null && appliedRules.length > 0
        && rateVariesAcrossDays(baseDaily, effectiveDaily, appliedRules, quotedTotal, days, chargeableDays),
    };
  }

  const bundleTotalSum = bundles.reduce((s, b) => s + b.total, 0);
  let bundleSavings = bundleBase - bundleTotalSum; // >0 = set is cheaper than parts
  // N.2: a rule on a bundle moves the parts too, so the saving is measured against the parts after
  // rules and the difference goes to the price-rules row. Without this a surcharge bigger than the
  // saving would read as a positive "bundle saving". The rows still add up to the grand total.
  bundles.forEach((b) => {
    if (!b.appliedRules.length || b.partsTotal == null) return;
    const ruleOnParts = b.partsTotal - (baseByBundle.get(b.key) || 0);
    ruleAdjustment += ruleOnParts;
    bundleSavings += ruleOnParts;
  });
  bundleSavings = round2(bundleSavings);
  ruleAdjustment = round2(ruleAdjustment);

  return {
    grandTotal: round2(quote.grandTotal),
    days: Number(quote.days) || 0,
    chargeableDays,
    baseTotal: round2(baseTotal),
    ruleAdjustment,
    bundleSavings,
    hasRules: Object.values(itemsById).some((v) => v.appliedRules.length > 0)
      || bundles.some((b) => b.appliedRules.length > 0),
    hasBundles: bundles.length > 0,
    hasAdjustments: Math.abs(ruleAdjustment) >= 0.01 || Math.abs(bundleSavings) >= 0.01,
    itemsById,
    bundles,
  };
}

// Groups the selected equipment for rendering: members of one matched bundle
// become a single group (kept in selection order) emitted at the position of
// their first member; every other unit is a one-item group. Without a
// breakdown each unit is its own group — the flat pre-P7.5 list.
export function groupEquipmentForDisplay(selectedEq, breakdown) {
  const list = Array.isArray(selectedEq) ? selectedEq : [];
  if (!breakdown) return list.map((eq) => ({ bundle: null, items: [eq] }));

  const emitted = new Set();
  const groups = [];
  for (const eq of list) {
    if (emitted.has(eq.id)) continue;
    const view = breakdown.itemsById[eq.id];
    const bundleKey = view ? view.bundleKey : null;
    if (bundleKey == null) {
      emitted.add(eq.id);
      groups.push({ bundle: null, items: [eq] });
      continue;
    }
    const bundle = breakdown.bundles[bundleKey];
    const members = list.filter((e) => bundle.equipmentIds.includes(e.id));
    members.forEach((m) => emitted.add(m.id));
    groups.push({ bundle, items: members });
  }
  return groups;
}
